<template>
  <b-tabs pills content-class="mt-2 mb-4" v-if="value">
    <b-tab active>
      <template slot="title">
        Generale
        <b-badge variant="danger"
          v-if="countErrors(['name', 'workgroup', 'description'])">
          {{ countErrors(['name', 'workgroup', 'description']) }}
        </b-badge>
      </template>
      <form>
        <string-form-field
          label="Nome"
          :value="value.name"
          required
          :disabled="disabled"
          :error="findError('name')"
          @input="propertyChanged('name', $event)"
        />

        <workgroup-form-field
          label="Gruppo di lavoro"
          :value="value.workgroup"
          :required="!isAdministrator()"
          :disabled="disabled"
          :error="findError('workgroup')"
          @input="propertyChanged('workgroup', $event)"
        />

        <string-form-field
          label="Descrizione"
          multiline
          :value="value.description"
          :disabled="disabled"
          :error="findError('description')"
          @input="propertyChanged('description', $event)"
        />
      </form>
    </b-tab>
    <b-tab class="pt-0">
      <template slot="title">
        Permessi
        <b-badge variant="danger"
          v-if="countErrors(['!name', '!workgroup', '!description', '!tags'])">
          {{ countErrors(['!name', '!workgroup', '!description', '!tags']) }}
        </b-badge>
      </template>
      <form>
        <template v-for="(macroGroup, index) in permissionGroups">
          <div v-if="index > 0" class="content-separator"></div>
          <div class="row">
            <div v-for="group in macroGroup" class="col">
              <h3 class="pt-4 pb-2">{{ group.title }}</h3>
              <template v-for="perm in group.permissions">
                <boolean-form-field
                  inline
                  :label="perm.description"
                  :value="value[perm.field]"
                  :disabled="disabled"
                  :error="findError(perm.field)"
                  @input="propertyChanged(perm.field, $event)"
                />
              </template>
            </div>
          </div>
        </template>
      </form>
    </b-tab>
    <b-tab>
      <template slot="title">
        Tag
        <b-badge variant="danger"
          v-if="countErrors(['tags'])">
          {{ countErrors(['tags']) }}
        </b-badge>
      </template>
      <tag-form-field
        :value="value.tags.map(x => x.tag)"
        :dropdown="false"
        multiple
        :disabled="disabled"
        :parameters="{
          nwi: true,
          workgroupIds: value.workgroup ? [value.workgroup.id] : null
        }"
        :error="findError('tags')"
        @input="tagsSelected"
      />
    </b-tab>
  </b-tabs>
</template>
<script>
import EntityEditorMixin from 'vue-core/mixins/EntityEditorMixin';
import isEqual from 'lodash/isEqual';
import sortBy from 'lodash/sortBy';

export default {
  mixins: [ EntityEditorMixin ],

  created() {
    this.permissionGroups = [
      // =======================================================================
      // Analisi principale
      // =======================================================================
      [
        {
          title: 'Analisi principale - Permessi del proprietario',
          permissions: [
            {
              field: 'permAnalysisOwnerLoad',
              description: 'Visualizzazione delle proprie analisi principali',
            },
            {
              field: 'permAnalysisOwnerInsert',
              description: 'Primo salvataggio delle proprie analisi principali',
            },
            {
              field: 'permAnalysisOwnerUpdate',
              description: 'Salvataggi successivi (modifica) delle proprie analisi principali',
            },
            {
              field: 'permAnalysisOwnerDelete',
              description: 'Eliminazione delle proprie analisi principali',
            },
            {
              field: 'permAnalysisOwnerManageShares',
              description: 'Gestione delle condivisioni delle proprie analisi principali',
            },
            {
              field: 'permAnalysisOwnerManageInvitations',
              description: 'Gestione degli inviti delle proprie analisi principali',
            },
            {
              field: 'permAnalysisOwnerReadMessages',
              description: 'Lettura messaggi relativi alle proprie analisi principali',
            },
            {
              field: 'permAnalysisOwnerSendMessages',
              description: 'Invio messaggi relativi alle proprie analisi principali',
            },
            {
              field: 'permAnalysisOwnerReceiveMessages',
              description: 'Ricezione via mail messaggi relativi alle proprie analisi principali',
            },
            {
              field: 'permAnalysisOwnerViewFiles',
              description: 'Visualizzazione file relativi alle proprie analisi principali',
            },
            {
              field: 'permAnalysisOwnerViewFilesWithNoTags',
              description: 'Visualizzazione file SENZA TAG relativi alle proprie analisi principali',
            },
            {
              field: 'permAnalysisOwnerUploadFiles',
              description: 'Caricamento file relativi alle proprie analisi principali',
            },
            {
              field: 'permAnalysisOwnerRenameFiles',
              description: 'Rinominazione file relativi alle proprie analisi principali',
            },
            {
              field: 'permAnalysisOwnerAddFileTags',
              description: 'Aggiunta tag a file relativi alle proprie analisi principali',
            },
            {
              field: 'permAnalysisOwnerRemoveFileTags',
              description: 'Rimozione tag da file relativi alle proprie analisi principali',
            },
            {
              field: 'permAnalysisOwnerDeleteFiles',
              description: 'Eliminazione file relativi alle proprie analisi principali',
            },
            {
              field: 'permAnalysisOwnerReceiveFiles',
              description: 'Ricezione via mail notifiche file relativi alle proprie analisi principali',
            },
          ],
        },
        {
          title: 'Analisi principale - Permessi sul gruppo di lavoro',
          permissions: [
            {
              field: 'permAnalysisGroupLoad',
              description: 'Visualizzazione delle analisi principali del gruppo di lavoro',
            },
            {
              field: 'permAnalysisGroupInsert',
              description: 'Primo salvataggio delle analisi principali del gruppo di lavoro',
            },
            {
              field: 'permAnalysisGroupUpdate',
              description: 'Salvataggi successivi (modifica) delle analisi principali del gruppo di lavoro',
            },
            {
              field: 'permAnalysisGroupDelete',
              description: 'Eliminazione delle analisi principali del gruppo di lavoro',
            },
            {
              field: 'permAnalysisGroupManageShares',
              description: 'Gestione delle condivisioni delle analisi principali del gruppo di lavoro',
            },
            {
              field: 'permAnalysisGroupManageInvitations',
              description: 'Gestione degli inviti delle analisi principali del gruppo di lavoro',
            },
            {
              field: 'permAnalysisGroupReadMessages',
              description: 'Lettura messaggi relativi alle analisi principali del gruppo di lavoro',
            },
            {
              field: 'permAnalysisGroupSendMessages',
              description: 'Invio messaggi relativi alle analisi principali del gruppo di lavoro',
            },
            {
              field: 'permAnalysisGroupReceiveMessages',
              description: 'Ricezione via mail messaggi relativi alle analisi principali del gruppo di lavoro',
            },
            {
              field: 'permAnalysisGroupViewFiles',
              description: 'Visualizzazione file relativi alle analisi principali del gruppo di lavoro',
            },
            {
              field: 'permAnalysisGroupViewFilesWithNoTags',
              description: 'Visualizzazione file SENZA TAG relativi alle analisi principali del gruppo di lavoro',
            },
            {
              field: 'permAnalysisGroupUploadFiles',
              description: 'Caricamento file relativi alle analisi principali del gruppo di lavoro',
            },
            {
              field: 'permAnalysisGroupRenameFiles',
              description: 'Rinominazione file relativi alle analisi principali del gruppo di lavoro',
            },
            {
              field: 'permAnalysisGroupAddFileTags',
              description: 'Aggiunta tag a file relativi alle analisi principali del gruppo di lavoro',
            },
            {
              field: 'permAnalysisGroupRemoveFileTags',
              description: 'Rimozione tag da file relativi alle analisi principali del gruppo di lavoro',
            },
            {
              field: 'permAnalysisGroupDeleteFiles',
              description: 'Eliminazione file relativi alle analisi principali del gruppo di lavoro',
            },
            {
              field: 'permAnalysisGroupReceiveFiles',
              description: 'Ricezione via mail notifiche file relativi alle analisi principali del gruppo di lavoro',
            },
          ]
        },
      ],
      // =======================================================================
      // Analisi di bilancio
      // =======================================================================
      [
        {
          title: 'Analisi di bilancio - Permessi del proprietario',
          permissions: [
            {
              field: 'permXbrlAnalysisOwnerLoad',
              description: 'Visualizzazione delle proprie analisi di bilancio',
            },
            {
              field: 'permXbrlAnalysisOwnerInsert',
              description: 'Primo salvataggio delle proprie analisi di bilancio',
            },
            {
              field: 'permXbrlAnalysisOwnerUpdate',
              description: 'Salvataggi successivi (modifica) delle proprie analisi di bilancio',
            },
            {
              field: 'permXbrlAnalysisOwnerDelete',
              description: 'Eliminazione delle proprie analisi di bilancio',
            },
          ],
        },
        {
          title: 'Analisi di bilancio - Permessi sul gruppo di lavoro',
          permissions: [
            {
              field: 'permXbrlAnalysisGroupLoad',
              description: 'Visualizzazione delle analisi di bilancio del gruppo di lavoro',
            },
            {
              field: 'permXbrlAnalysisGroupInsert',
              description: 'Primo salvataggio delle analisi di bilancio del gruppo di lavoro',
            },
            {
              field: 'permXbrlAnalysisGroupUpdate',
              description: 'Salvataggi successivi (modifica) delle analisi di bilancio del gruppo di lavoro',
            },
            {
              field: 'permXbrlAnalysisGroupDelete',
              description: 'Eliminazione delle analisi di bilancio del gruppo di lavoro',
            },
          ]
        },
      ],
      // =======================================================================
      // Analisi CR
      // =======================================================================
      [
        {
          title: 'Analisi CR - Permessi del proprietario',
          permissions: [
            {
              field: 'permCrAnalysisOwnerLoad',
              description: 'Visualizzazione delle proprie analisi CR',
            },
            {
              field: 'permCrAnalysisOwnerInsert',
              description: 'Primo salvataggio delle proprie analisi CR',
            },
            {
              field: 'permCrAnalysisOwnerUpdate',
              description: 'Salvataggi successivi (modifica) delle proprie analisi CR',
            },
            {
              field: 'permCrAnalysisOwnerDelete',
              description: 'Eliminazione delle proprie analisi CR',
            },
          ]
        },
        {
          title: 'Analisi CR - Permessi sul gruppo di lavoro',
          permissions: [
            {
              field: 'permCrAnalysisGroupLoad',
              description: 'Visualizzazione delle analisi CR del gruppo di lavoro',
            },
            {
              field: 'permCrAnalysisGroupInsert',
              description: 'Primo salvataggio delle analisi CR del gruppo di lavoro',
            },
            {
              field: 'permCrAnalysisGroupUpdate',
              description: 'Salvataggi successivi (modifica) delle analisi CR del gruppo di lavoro',
            },
            {
              field: 'permCrAnalysisGroupDelete',
              description: 'Eliminazione delle analisi CR del gruppo di lavoro',
            },
          ]
        },
      ],
      // =======================================================================
      // Analisi CRIF
      // =======================================================================
      [
        {
          title: 'Analisi CRIF - Permessi del proprietario',
          permissions: [
            {
              field: 'permCrifAnalysisOwnerLoad',
              description: 'Visualizzazione delle proprie analisi CRIF',
            },
            {
              field: 'permCrifAnalysisOwnerInsert',
              description: 'Primo salvataggio delle proprie analisi CRIF',
            },
            {
              field: 'permCrifAnalysisOwnerUpdate',
              description: 'Salvataggi successivi (modifica) delle proprie analisi CRIF',
            },
            {
              field: 'permCrifAnalysisOwnerDelete',
              description: 'Eliminazione delle proprie analisi CRIF',
            },
          ]
        },
        {
          title: 'Analisi CRIF - Permessi sul gruppo di lavoro',
          permissions: [
            {
              field: 'permCrifAnalysisGroupLoad',
              description: 'Visualizzazione delle analisi CRIF del gruppo di lavoro',
            },
            {
              field: 'permCrifAnalysisGroupInsert',
              description: 'Primo salvataggio delle analisi CRIF del gruppo di lavoro',
            },
            {
              field: 'permCrifAnalysisGroupUpdate',
              description: 'Salvataggi successivi (modifica) delle analisi CRIF del gruppo di lavoro',
            },
            {
              field: 'permCrifAnalysisGroupDelete',
              description: 'Eliminazione delle analisi CRIF del gruppo di lavoro',
            },
          ]
        },
      ]
    ]
  },

  methods: {
    tagsSelected(tags) {
      const oldTags = sortBy(this.value.tags.map(t => t.tag.id));
      const newTags = sortBy(tags.map(t => t.id));

      if (!isEqual(oldTags, newTags)) {
        this.$emit('input', {
          ...this.value,
          tags: tags.map(t => ({ tag: t }))
        });
      }
    },
  }
}
</script>
